/**
 * Lista de valores distintos: cache, portal y teclado.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { engineFetch, engineErrorMessage, subscribeEngineData } from './engineConfig';
import { studio, ink, pillTone } from '../studioUi';

const DISTINCT_LIMIT = 400;
const cache = new Map();
// Tras «Actualizar» (o el refresco automático) las listas guardadas pueden estar viejas.
subscribeEngineData(() => cache.clear());

function sourceKey(dataSource, column, search = '') {
    return JSON.stringify({
        q: search,
        sid: dataSource?.spreadsheet_id || '',
        sheet: dataSource?.sheet_name || '',
        ds: dataSource?.data_source_id || '',
        col: column || '',
        filters: (dataSource?.data_sources || []).map((row) => [row.id, row.filters || []]),
    });
}

function placeMenu(anchor) {
    if (!anchor) return { top: 0, left: 0, width: 240 };
    const rect = anchor.getBoundingClientRect();
    const width = Math.min(Math.max(rect.width, 248), Math.max(240, window.innerWidth - 16));
    let left = Math.min(rect.left, window.innerWidth - width - 8);
    left = Math.max(8, left);
    const estimated = 300;
    const below = rect.bottom + 6;
    const top = below + estimated > window.innerHeight - 8
        ? Math.max(8, rect.top - estimated - 6)
        : below;
    return { top, left, width };
}

const FilterDropdown = ({
    label,
    value = [],
    options = [],
    optionsComplete = false,
    onChange,
    dataSource,
    column,
    placeholder = 'Todos',
    multiple = true,
    compact = false,
    allowCustom = true,
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [remote, setRemote] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 240 });
    const [highlight, setHighlight] = useState(0);
    const boxRef = useRef(null);
    const triggerRef = useRef(null);
    const searchRef = useRef(null);
    const selected = Array.isArray(value) ? value.map(String) : (value ? [String(value)] : []);
    const dsRef = useRef(dataSource);
    dsRef.current = dataSource;
    // Solo se consulta al motor si faltan valores, o si el usuario busca fuera de la lista.
    // La búsqueda viaja al motor: con más de DISTINCT_LIMIT valores, lo que no cupo
    // en la primera lista solo se encuentra así.
    const term = search.trim();
    const needsRemote = !optionsComplete || Boolean(term);
    const cacheId = useMemo(() => sourceKey(dataSource, column, term), [dataSource, column, term]);

    /* Al cambiar de columna o de fuente, los valores traídos para la anterior ya no aplican. */
    const baseKey = useMemo(() => sourceKey(dataSource, column), [dataSource, column]);
    useEffect(() => {
        setRemote([]);
        setLoadError('');
    }, [baseKey]);

    const syncPos = useCallback(() => {
        setMenuPos(placeMenu(triggerRef.current));
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        const close = (event) => {
            if (boxRef.current && boxRef.current.contains(event.target)) return;
            if (triggerRef.current && triggerRef.current.contains(event.target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', close);
        window.addEventListener('resize', syncPos);
        window.addEventListener('scroll', syncPos, true);
        syncPos();
        const focus = setTimeout(() => searchRef.current?.focus(), 0);
        return () => {
            document.removeEventListener('mousedown', close);
            window.removeEventListener('resize', syncPos);
            window.removeEventListener('scroll', syncPos, true);
            clearTimeout(focus);
        };
    }, [open, syncPos]);

    useEffect(() => {
        if (!open || !column || (!dataSource?.spreadsheet_id && !dataSource?.data_source_id)) return undefined;
        // Con la lista completa guardada al publicar no hace falta pedir nada al motor.
        if (!needsRemote) return undefined;
        const hit = cache.get(cacheId);
        if (hit) {
            setRemote(hit);
            setLoadError('');
            setLoading(false);
            return undefined;
        }
        const controller = new AbortController();
        const handle = setTimeout(async () => {
            setLoading(true);
            try {
                const ds = dsRef.current || {};
                const response = await engineFetch('/api/engine/distinct', {
                    method: 'POST',
                    signal: controller.signal,
                    body: JSON.stringify({
                        spreadsheet_id: ds.spreadsheet_id || '',
                        sheet_name: ds.sheet_name || '',
                        data_source_id: ds.data_source_id,
                        data_sources: ds.data_sources,
                        joins: ds.joins,
                        column,
                        ...(term ? { search: term } : {}),
                        limit: DISTINCT_LIMIT,
                    }),
                });
                if (!response.ok) {
                    throw new Error(await engineErrorMessage(response, 'No se pudieron cargar valores.'));
                }
                const res = await response.json();
                if (!res.success) {
                    throw new Error(res.detail || res.error || 'No se pudieron cargar valores.');
                }
                const values = res.values || [];
                cache.set(cacheId, values);
                if (cache.size > 48) cache.delete(cache.keys().next().value);
                setRemote(values);
                setLoadError('');
            } catch (err) {
                if (err.name === 'AbortError') return;
                setLoadError(err.message || 'No se pudieron cargar valores del filtro.');
            } finally {
                setLoading(false);
            }
        }, term ? 250 : 80);
        return () => {
            clearTimeout(handle);
            controller.abort();
        };
    }, [open, column, cacheId, needsRemote, term]);

    const merged = useMemo(() => {
        const set = new Set([...options.map(String), ...remote.map(String), ...selected]);
        return Array.from(set);
    }, [options, remote, selected]);

    const needle = search.trim().toLowerCase();
    const visible = merged.filter((item) => !needle || item.toLowerCase().includes(needle));
    const summary = selected.length === 0
        ? placeholder
        : (selected.length === 1 ? selected[0] : `${selected.length} seleccionados`);

    const toggle = (item) => {
        if (!multiple) {
            onChange(selected.includes(item) ? [] : [item]);
            setOpen(false);
            return;
        }
        if (selected.includes(item)) onChange(selected.filter((v) => v !== item));
        else onChange([...selected, item]);
    };

    const applyTypedValue = () => {
        const typed = search.trim();
        if (!typed) return;
        if (multiple) {
            if (!selected.includes(typed)) onChange([...selected, typed]);
        } else {
            onChange([typed]);
            setOpen(false);
        }
        setSearch('');
    };

    const onKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            setOpen(false);
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlight((i) => Math.min(visible.length - 1, i + 1));
            return;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlight((i) => Math.max(0, i - 1));
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            if (visible[highlight]) toggle(visible[highlight]);
            else if (allowCustom && search.trim()) applyTypedValue();
        }
    };

    const menu = open ? createPortal(
        <div
            ref={boxRef}
            role="listbox"
            aria-multiselectable={multiple}
            style={{ ...styles.menu, top: menuPos.top, left: menuPos.left, width: menuPos.width }}
            onKeyDown={onKeyDown}
        >
            <input
                ref={searchRef}
                value={search}
                onChange={(e) => { setSearch(e.target.value); setHighlight(0); }}
                placeholder="Buscar valor…"
                style={styles.search}
            />
            <div style={styles.actions}>
                {multiple && (
                    <button type="button" style={styles.link} onClick={() => onChange(Array.from(new Set([...selected, ...visible])))} disabled={!visible.length}>
                        Todos visibles
                    </button>
                )}
                <button type="button" style={styles.link} onClick={() => onChange([])} disabled={!selected.length}>
                    Limpiar
                </button>
            </div>
            {selected.length > 0 && (
                <div style={styles.chips}>
                    {selected.slice(0, 8).map((item) => (
                        <button key={item} type="button" style={studio.filterChip} onClick={() => toggle(item)} title="Quitar">
                            {item}
                            <span style={studio.filterChipX}>×</span>
                        </button>
                    ))}
                    {selected.length > 8 && <span style={styles.more}>+{selected.length - 8}</span>}
                </div>
            )}
            <div style={styles.list}>
                {loading && <div style={styles.empty}>Cargando valores…</div>}
                {loadError && <div style={styles.loadError}>{loadError}</div>}
                {!loading && visible.length === 0 && !loadError && <div style={styles.empty}>Sin coincidencias</div>}
                {visible.map((item, index) => {
                    const on = selected.includes(item);
                    return (
                        <button
                            key={item}
                            type="button"
                            role="option"
                            aria-selected={on}
                            style={{
                                ...styles.option,
                                backgroundColor: index === highlight ? ink.wash : 'transparent',
                            }}
                            onMouseEnter={() => setHighlight(index)}
                            onClick={() => toggle(item)}
                        >
                            <span style={{ ...styles.check, ...(on ? styles.checkOn : {}) }}>{on ? '✓' : ''}</span>
                            <span style={styles.optionLabel}>{item}</span>
                        </button>
                    );
                })}
                {allowCustom && search.trim() && !merged.some((item) => item.toLowerCase() === search.trim().toLowerCase()) && (
                    <button type="button" style={styles.customBtn} onClick={applyTypedValue}>
                        Usar «{search.trim()}»
                    </button>
                )}
            </div>
        </div>,
        document.body,
    ) : null;

    return (
        <div style={compact ? styles.wrapCompact : styles.wrap}>
            {label ? <span style={studio.fieldLabel}>{label}</span> : null}
            <button
                ref={triggerRef}
                type="button"
                style={{ ...styles.trigger, ...pillTone(selected.length > 0) }}
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                aria-haspopup="listbox"
            >
                <span style={styles.summary}>{summary}</span>
                {selected.length > 1 ? <span style={styles.badge}>{selected.length}</span> : null}
                <span style={{ color: ink.accent, flexShrink: 0 }}>{open ? '▴' : '▾'}</span>
            </button>
            {menu}
        </div>
    );
};

const styles = {
    wrap: { position: 'relative', minWidth: '180px', flex: 1 },
    wrapCompact: { position: 'relative', minWidth: 0, width: '100%' },
    summary: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, textAlign: 'left' },
    badge: {
        flexShrink: 0,
        minWidth: 18,
        height: 18,
        borderRadius: 999,
        backgroundColor: ink.accent,
        color: ink.surface,
        fontSize: '0.68rem',
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 5px',
    },
    customBtn: {
        width: '100%',
        marginTop: 6,
        borderWidth: 1,
        borderStyle: 'dashed',
        borderColor: ink.lineStrong,
        backgroundColor: ink.accentSoft,
        color: ink.accentText,
        borderRadius: 10,
        padding: '8px 10px',
        cursor: 'pointer',
        fontSize: '0.75rem',
        fontWeight: 600,
        fontFamily: 'inherit',
    },
    trigger: {
        ...studio.filterPill,
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 8,
        paddingRight: 14,
        backgroundImage: 'none',
        minHeight: 38,
        boxSizing: 'border-box',
    },
    menu: {
        position: 'fixed',
        zIndex: 80,
        backgroundColor: ink.surface,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: 16,
        boxShadow: '0 12px 32px rgba(28,29,33,0.16)',
        padding: 10,
        boxSizing: 'border-box',
    },
    search: {
        ...studio.controlCompact,
        marginBottom: 8,
        width: '100%',
        boxSizing: 'border-box',
    },
    actions: { display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 6 },
    link: {
        borderWidth: 0,
        borderStyle: 'solid',
        borderColor: 'transparent',
        backgroundColor: 'transparent',
        color: ink.accent,
        cursor: 'pointer',
        fontSize: '0.75rem',
        fontWeight: 700,
        fontFamily: 'inherit',
        padding: 0,
    },
    chips: { display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 },
    more: { fontSize: '0.72rem', color: ink.faint, fontWeight: 700, alignSelf: 'center' },
    list: { maxHeight: 220, overflowX: 'hidden', overflowY: 'auto', overscrollBehavior: 'contain' },
    option: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        textAlign: 'left',
        padding: '7px 6px',
        borderWidth: 0,
        borderStyle: 'solid',
        borderColor: 'transparent',
        backgroundColor: 'transparent',
        borderRadius: 8,
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: '0.82rem',
        color: ink.text,
    },
    check: {
        width: 16,
        height: 16,
        borderRadius: 4,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        backgroundColor: ink.surface,
        flexShrink: 0,
        fontSize: '0.65rem',
        lineHeight: '16px',
        textAlign: 'center',
        color: ink.surface,
    },
    checkOn: {
        backgroundColor: ink.accent,
        borderColor: ink.accent,
    },
    optionLabel: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    empty: { fontSize: '0.8rem', color: ink.faint, padding: 8 },
    loadError: { fontSize: '0.8rem', color: ink.danger, padding: 8, fontWeight: 600 },
};

export default FilterDropdown;
